import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import ConstructionProperties from './construction-properties.component';
import { SimpleConstructionProperty, SimpleSubCategory } from 'models/order-input/simple-order';
import { RestorationInput, RestorationTeethInput } from 'models/order-input/restoration-input';
import { TeethSelectionType } from 'models/order';
import { TeethRange } from 'models/material';
import TeethSelection, { SelectionMode } from '../../teeth-selection/teeth-selection.component';
import { ConstructionPropertyType } from 'models/order-input/shared';
import {
    PROPERTY_KEY_BY_FIELD,
    RESTORATION_TYPE_BY_SIMPLE_CATEGORY,
    propertiesFromInputs,
    propertiesToInputs,
    jawPickerRangeOfTeeth,
    jawTeethInputFromRange,
    simpleCategoryForType,
    teethInputFromNumbers,
    teethNumbersFromRestoration,
} from 'components/order-flow/shared/restoration-input-helper';

/** The keys the property widgets edit; the dialog owns WARRANTY_TYPE. */
const WIDGET_KEYS = new Set(Object.values(PROPERTY_KEY_BY_FIELD).filter((key) => key !== 'WARRANTY_TYPE'));

type ConstructionFormProps = {
    category: SimpleSubCategory;
    /** The restoration being edited, or one prefilled by the AI assistant; empty for a new one. */
    restoration?: RestorationInput;
    onRestorationChange?: (restoration: RestorationInput | undefined) => void;
};
const ConstructionForm: FC<ConstructionFormProps> = ({ category, restoration, onRestorationChange }) => {
    const [selectedTeeth, setSelectedTeeth] = useState<number[]>([]);
    const [selectedTeethRange, setSelectedTeethRange] = useState<TeethRange>();
    const [currentSettings, setCurrentSettings] = useState<SimpleConstructionProperty>(
        {} as SimpleConstructionProperty,
    );
    const settings = useMemo(
        () => (restoration ? (propertiesFromInputs(restoration.properties) as SimpleConstructionProperty) : undefined),
        [restoration],
    );

    useEffect(() => {
        const { teeth, teethRange } = pickerTeeth(restoration?.teeth, selectionMode());
        setSelectedTeeth(teeth);
        setSelectedTeethRange(teethRange);
    }, [restoration]);

    useEffect(() => {
        if (!onRestorationChange) return;
        if (!isPropertiesValid() || (selectedTeeth.length === 0 && !selectedTeethRange)) {
            onRestorationChange(undefined);
            return;
        }
        const base = restoration ?? { properties: [], actions: [], materials: [] };
        // A stored type the simple flow has no tile for (a veneer shows as "other") stays as it is.
        const type =
            restoration?.type && simpleCategoryForType(restoration.type) === category
                ? restoration.type
                : RESTORATION_TYPE_BY_SIMPLE_CATEGORY[category];
        onRestorationChange({
            ...base,
            type,
            teeth: selectedTeethRange
                ? jawTeethInputFromRange(selectedTeethRange)
                : teethInputFromNumbers(selectedTeeth),
            properties: [
                ...base.properties.filter((property) => !WIDGET_KEYS.has(property.key)),
                ...propertiesToInputs(currentSettings),
            ],
        });
    }, [selectedTeeth, selectedTeethRange, currentSettings]);

    const isShadeSelectionValid = () => {
        if (currentSettings.shade) {
            return Boolean(currentSettings.color || currentSettings.colorSampling);
        }
        // An empty shade with a color means the color was entered as free text
        return Boolean(currentSettings.color);
    };

    const isPropertiesValid = () => {
        if (
            category === SimpleSubCategory.SINGLE_CROWN ||
            category === SimpleSubCategory.BRIDGE ||
            category === SimpleSubCategory.IMPLANT_CROWN ||
            category === SimpleSubCategory.PARTIAL_DENTURE ||
            category === SimpleSubCategory.TEMPORARY_DENTURE ||
            category === SimpleSubCategory.COMPLETE_DENTURE
        ) {
            return (currentSettings.material || currentSettings.otherMaterial) && isShadeSelectionValid();
        }
        if (category === SimpleSubCategory.POST || category === SimpleSubCategory.BITETRAY) {
            return currentSettings.material || currentSettings.otherMaterial;
        }
        if (category === SimpleSubCategory.IMPLANT_BRIDGE) {
            return (
                (currentSettings.material || currentSettings.otherMaterial) &&
                isShadeSelectionValid() &&
                currentSettings.retainment
            );
        }
        return true;
    };

    const selectionMode = () => {
        switch (category) {
            case SimpleSubCategory.BRIDGE:
            case SimpleSubCategory.IMPLANT_BRIDGE:
            case SimpleSubCategory.PARTIAL_DENTURE:
            case SimpleSubCategory.COMPLETE_DENTURE:
            case SimpleSubCategory.OTHER_REMOVABLE:
                return SelectionMode.RANGE;
            case SimpleSubCategory.BITETRAY:
                return SelectionMode.JAW;
            default:
                return SelectionMode.SINGLE;
        }
    };

    const handleTeethSelection = (teeth?: number[], teethRange?: TeethRange) => {
        if (teethRange) {
            setSelectedTeethRange(teethRange);
            setSelectedTeeth([]);
            return;
        }
        if (teeth && teeth.length > 0) {
            setSelectedTeeth(teeth);
            setSelectedTeethRange(undefined);
            return;
        }
        // The jaw picker reports neither when the last jaw is taken out of the selection.
        if (!teeth) {
            setSelectedTeeth([]);
            setSelectedTeethRange(undefined);
        }
    };

    return (
        <Box>
            <Box mb={2} maxWidth={1200}>
                <TeethSelection
                    teeth={selectedTeeth}
                    teethRange={selectedTeethRange}
                    onChange={handleTeethSelection}
                    selectionMode={selectionMode()}
                />
            </Box>
            <Box>
                <ConstructionProperties
                    properties={categoryConstructionPropertyMap[category]}
                    onSettingsChange={(settings) => setCurrentSettings(settings)}
                    availableMaterials={materialsForCategory[category]}
                    settings={settings}
                />
            </Box>
        </Box>
    );
};

/** The tooth picker's selection for a restoration's teeth, in the picker's mode. */
const pickerTeeth = (
    teeth: RestorationTeethInput | undefined,
    mode: SelectionMode,
): { teeth: number[]; teethRange?: TeethRange } => {
    if (!teeth || teeth.type === TeethSelectionType.NONE) return { teeth: [] };
    if (teeth.type === TeethSelectionType.RANGE && teeth.selectedRange) {
        return { teeth: [], teethRange: teeth.selectedRange };
    }
    // A jaw picked as a jaw loads back as the range the picker holds it as (DD-3765); a jaw
    // clicked tooth by tooth lists its teeth and loads back as those.
    const jawRange = mode === SelectionMode.JAW ? jawPickerRangeOfTeeth(teeth) : undefined;
    if (jawRange) return { teeth: [], teethRange: jawRange };
    const { teethNumbers } = teethNumbersFromRestoration(teeth);
    if (mode === SelectionMode.SINGLE || !teethNumbers.length) return { teeth: teethNumbers };
    return {
        teeth: mode === SelectionMode.JAW ? teethNumbers : [],
        teethRange: { start: teethNumbers[0], end: teethNumbers[teethNumbers.length - 1] },
    };
};

const categoryConstructionPropertyMap: Record<SimpleSubCategory, ConstructionPropertyType[]> = {
    [SimpleSubCategory.SINGLE_CROWN]: [
        ConstructionPropertyType.SHADE,
        ConstructionPropertyType.MATERIAL,
        ConstructionPropertyType.METAL_DESIGN,
        ConstructionPropertyType.OCCLUSION_TYPE,
        ConstructionPropertyType.CONTACT_TYPE,
    ],
    [SimpleSubCategory.BRIDGE]: [
        ConstructionPropertyType.SHADE,
        ConstructionPropertyType.MATERIAL,
        ConstructionPropertyType.METAL_DESIGN,
        ConstructionPropertyType.OCCLUSION_TYPE,
        ConstructionPropertyType.CONTACT_TYPE,
        ConstructionPropertyType.PONTIC_DESIGN,
    ],
    [SimpleSubCategory.POST]: [ConstructionPropertyType.MATERIAL],
    [SimpleSubCategory.IMPLANT_CROWN]: [
        ConstructionPropertyType.SHADE,
        ConstructionPropertyType.MATERIAL,
        ConstructionPropertyType.METAL_DESIGN,
        ConstructionPropertyType.OCCLUSION_TYPE,
        ConstructionPropertyType.CONTACT_TYPE,
    ],
    [SimpleSubCategory.IMPLANT_BRIDGE]: [
        ConstructionPropertyType.SHADE,
        ConstructionPropertyType.MATERIAL,
        ConstructionPropertyType.METAL_DESIGN,
        ConstructionPropertyType.OCCLUSION_TYPE,
        ConstructionPropertyType.CONTACT_TYPE,
        ConstructionPropertyType.PONTIC_DESIGN,
        ConstructionPropertyType.RETAINMENT,
    ],
    [SimpleSubCategory.OTHER]: [],
    [SimpleSubCategory.COMPLETE_DENTURE]: [ConstructionPropertyType.SHADE, ConstructionPropertyType.MATERIAL],
    [SimpleSubCategory.PARTIAL_DENTURE]: [ConstructionPropertyType.SHADE, ConstructionPropertyType.MATERIAL],
    [SimpleSubCategory.TEMPORARY_DENTURE]: [ConstructionPropertyType.SHADE, ConstructionPropertyType.MATERIAL],
    [SimpleSubCategory.BITETRAY]: [ConstructionPropertyType.BITETRAY_MATERIAL],
    [SimpleSubCategory.OTHER_REMOVABLE]: [],
};

const materialsForCategory: Record<SimpleSubCategory, string[]> = {
    [SimpleSubCategory.SINGLE_CROWN]: [
        'Lithium Disilicate',
        'Empress',
        'Zirconia',
        'Guld',
        'MK-Guld',
        'MK-Titan',
        'MK-CoCr',
    ],
    [SimpleSubCategory.BRIDGE]: ['Lithium Disilicate', 'Empress', 'Zirconia', 'Guld', 'MK-Guld', 'MK-Titan', 'MK-CoCr'],
    [SimpleSubCategory.POST]: ['Titan', 'Porslin', 'Guld', 'Cobolt Krom'],
    [SimpleSubCategory.IMPLANT_CROWN]: ['Zirconia', 'MK-Titan', 'MK-CoCr'],
    [SimpleSubCategory.IMPLANT_BRIDGE]: ['Zirconia', 'MK-Titan', 'MK-CoCr'],
    [SimpleSubCategory.OTHER]: [],
    [SimpleSubCategory.COMPLETE_DENTURE]: ['Akryl', 'CoCr'],
    [SimpleSubCategory.PARTIAL_DENTURE]: ['Akryl', 'CoCr'],
    [SimpleSubCategory.TEMPORARY_DENTURE]: ['Akryl', 'Valplast'],
    [SimpleSubCategory.BITETRAY]: [],
    [SimpleSubCategory.OTHER_REMOVABLE]: [],
};

export default ConstructionForm;
